"use client";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { motion, AnimatePresence } from "framer-motion";
import {
  Clock,
  CreditCard,
  FileText,
  Check,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { useRouter } from "@/navigation";
import { useOwnerSubscriptionActions } from "@/hooks/useOwnerSubscriptionActions";
import {
  OwnerSubscription,
  PaymentMethodStatus,
  BillingProfile,
} from "@/server-side/types/subscription";

interface Props {
  venueId: string | null;
}

// Under this many days the pill escalates from informational to amber.
const URGENT_DAYS = 14;
// Under this, red.
const CRITICAL_DAYS = 3;

type Tone = "neutral" | "warn" | "danger";

interface PillView {
  tone: Tone;
  label: string;
  // The pill only earns room for the whole phrase once the nav row is wide.
  // Both forms are rendered and CSS picks between them, so the choice costs
  // nothing at runtime and is right on the very first paint. Omitted when the
  // label is already short enough to survive a narrow bar.
  labelShort?: string;
  title: string;
  hint: string;
  showChecklist: boolean;
}

const TONE_PILL: Record<Tone, string> = {
  neutral: "bg-violet-500/10 border-violet-500/30 text-violet-200 hover:bg-violet-500/15",
  warn: "bg-amber-500/10 border-amber-500/30 text-amber-200 hover:bg-amber-500/15",
  danger: "bg-red-500/10 border-red-500/30 text-red-200 hover:bg-red-500/15",
};

const TONE_BAR: Record<Tone, string> = {
  neutral: "bg-violet-400",
  warn: "bg-amber-400",
  danger: "bg-red-400",
};

const daysUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  return Math.ceil((then - Date.now()) / (1000 * 60 * 60 * 24));
};

// Subscription health, surfaced in the nav bar where it is always in view.
//
// Only shown when there is something to say: a trial running down, a failed
// payment, a paused venue, a scheduled cancellation. A healthy paid
// subscription renders nothing rather than a permanent "Active" chip that
// nobody needs to see every day.
const SubscriptionStatus: React.FC<Props> = ({ venueId }) => {
  const t = useTranslations("SubscriptionStatus");
  const locale = useLocale();
  const router = useRouter();
  const actions = useOwnerSubscriptionActions();

  const [sub, setSub] = useState<OwnerSubscription | null>(null);
  const [pm, setPm] = useState<PaymentMethodStatus | null>(null);
  const [billing, setBilling] = useState<BillingProfile | null>(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;

    const load = async () => {
      // The setup checklist is part of the pill's own signal (an unset card is
      // worth flagging before it is opened), so all three load together.
      const [subRes, pmRes, billingRes] = await Promise.all([
        actions.getOwnerSubscription(venueId),
        actions.getOwnerPaymentMethod(venueId),
        actions.getBillingProfile(venueId),
      ]);
      if (cancelled) return;
      if (subRes.success) setSub(subRes.data ?? null);
      if (pmRes.success) setPm(pmRes.data ?? null);
      if (billingRes.success) setBilling(billingRes.data ?? null);
    };

    load();
    return () => {
      cancelled = true;
    };
    // actions is re-created each render; including it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const fmtDate = useCallback(
    (iso: string | null | undefined): string =>
      iso ? DateTime.fromISO(iso).setLocale(locale).toFormat("d LLL yyyy") : "—",
    [locale]
  );

  const hasCard = !!pm?.hasCard;
  const hasBilling = !!billing?.complete;
  const setupDone = hasCard && hasBilling;

  const view = useMemo<PillView | null>(() => {
    if (!sub) return null;

    const left = daysUntil(sub.trialEnd);

    if (sub.status === "EXPIRED") {
      return {
        tone: "danger" as Tone,
        label: t("Pill.Paused"),
        title: t("Popover.ExpiredTitle"),
        hint: t("Popover.ExpiredHint"),
        showChecklist: true,
      };
    }
    if (sub.status === "PAST_DUE") {
      return {
        tone: "danger" as Tone,
        label: t("Pill.PaymentFailed"),
        title: t("Popover.PastDueTitle"),
        hint: t("Popover.PastDueHint"),
        showChecklist: false,
      };
    }
    if (sub.cancelAtPeriodEnd) {
      const until = sub.status === "TRIALING" ? sub.trialEnd : sub.currentPeriodEnd;
      return {
        tone: "warn" as Tone,
        label: t("Pill.Cancelling"),
        title: t("Popover.CancellingTitle"),
        hint: t("Popover.CancellingHint", { date: fmtDate(until) }),
        showChecklist: false,
      };
    }
    if (sub.status === "TRIALING") {
      // An unfinished setup is itself the warning, whatever the day count says.
      const tone: Tone =
        left != null && left <= CRITICAL_DAYS
          ? "danger"
          : (left != null && left <= URGENT_DAYS) || !setupDone
            ? "warn"
            : "neutral";
      const running = left != null && left > 0;
      return {
        tone,
        label: running
          ? t("Pill.DaysLeft", { count: left })
          : t("Pill.EndsToday"),
        labelShort: running
          ? t("Pill.DaysShort", { count: left })
          : t("Pill.EndsToday"),
        title: t("Popover.TrialTitle"),
        hint: t("Popover.EndsOn", { date: fmtDate(sub.trialEnd) }),
        showChecklist: true,
      };
    }
    // ACTIVE and CANCELLED-and-done need no permanent nav real estate.
    return null;
  }, [sub, setupDone, t, fmtDate]);

  // Fraction of the trial already used, for the progress bar.
  const trialProgress = useMemo(() => {
    if (!sub?.trialEnd || !sub.createdAt) return null;
    const start = new Date(sub.createdAt).getTime();
    const end = new Date(sub.trialEnd).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
    return Math.min(1, Math.max(0, (Date.now() - start) / (end - start)));
  }, [sub]);

  if (!venueId || !view) return null;

  const goToSubscription = () => {
    setOpen(false);
    router.push({
      pathname: "/venues/[venueId]/subscription",
      params: { venueId },
    });
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t("AriaLabel")}
        aria-expanded={open}
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
          TONE_PILL[view.tone]
        }`}
      >
        <Clock className="w-3.5 h-3.5 shrink-0" />
        {/* Both forms ship; the breakpoint decides. Choosing in JS would mean
            the pill could not settle until a media query had run, which is one
            paint after the bar is already on screen. */}
        <span className="whitespace-nowrap xl:hidden">
          {view.labelShort ?? view.label}
        </span>
        <span className="whitespace-nowrap hidden xl:inline">{view.label}</span>
        {/* A quiet dot beats a second line of text for "there is a step left". */}
        {view.showChecklist && !setupDone && (
          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 mt-2 w-72 rounded-xl bg-gray-800/95 backdrop-blur-md border border-gray-700 shadow-xl z-50 overflow-hidden"
          >
            <div className="px-4 py-3 border-b border-gray-700/70">
              <div className="text-[0.62rem] uppercase tracking-[0.12em] font-semibold text-gray-500">
                {view.title}
              </div>
              <div className="text-sm font-semibold text-gray-100 mt-1">
                {view.label}
              </div>
              <div className="text-xs text-gray-400 mt-0.5">{view.hint}</div>

              {trialProgress != null && sub?.status === "TRIALING" && (
                <div className="mt-2.5 h-1 rounded-full bg-gray-700/70 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${TONE_BAR[view.tone]}`}
                    style={{ width: `${Math.round(trialProgress * 100)}%` }}
                  />
                </div>
              )}
            </div>

            {view.showChecklist && (
              <div className="px-4 py-3 space-y-2">
                <div className="text-[0.62rem] uppercase tracking-[0.12em] font-semibold text-gray-500">
                  {setupDone ? t("Checklist.DoneTitle") : t("Checklist.Title")}
                </div>
                <ChecklistRow
                  icon={<FileText className="w-3.5 h-3.5" />}
                  label={t("Checklist.Billing")}
                  done={hasBilling}
                  doneLabel={t("Checklist.Done")}
                  missingLabel={t("Checklist.Missing")}
                />
                <ChecklistRow
                  icon={<CreditCard className="w-3.5 h-3.5" />}
                  label={t("Checklist.Card")}
                  done={hasCard}
                  doneLabel={t("Checklist.Done")}
                  missingLabel={t("Checklist.Missing")}
                />
              </div>
            )}

            <div className="px-4 pb-3 pt-1">
              <button
                onClick={goToSubscription}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-500 px-3 py-2 text-sm font-medium text-white transition-colors"
              >
                {setupDone ? t("Cta.Manage") : t("Cta.Finish")}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const ChecklistRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  done: boolean;
  doneLabel: string;
  missingLabel: string;
}> = ({ icon, label, done, doneLabel, missingLabel }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2 min-w-0 text-gray-300">
      <span className={done ? "text-emerald-400" : "text-amber-400"}>{icon}</span>
      <span className="text-xs truncate">{label}</span>
    </div>
    <span
      className={`inline-flex items-center gap-1 text-[0.68rem] font-medium shrink-0 ${
        done ? "text-emerald-300" : "text-amber-300"
      }`}
    >
      {done ? (
        <Check className="w-3 h-3" />
      ) : (
        <AlertTriangle className="w-3 h-3" />
      )}
      {done ? doneLabel : missingLabel}
    </span>
  </div>
);

export default SubscriptionStatus;
